import * as React from 'react';

import { PaymentType } from '@rematter/frontend-lib-api-client';
import { MultiSelectFilter } from '@rematter/ui-web';
import { useAtom, useSetAtom } from 'jotai';

import { FilterLabel } from '../../_shared/FilterLabels';

import {
  $columnSelectedTypes,
  $offset,
} from '~/modules/finance/_state/financePayments.state';
import { useTranslations } from '~/modules/finance/i18n/useTranslations';

interface TypeOption {
  id: PaymentType;
  label: string;
}

interface PaymentTypeColumnFilterProps {
  onClose?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ PAYMENT TYPE COLUMN FILTER ⚛️ ⚛️ ⚛️
//
export const PaymentTypeColumnFilter: React.FC<
  PaymentTypeColumnFilterProps
> = ({ onClose }) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const [selectedTypesSet, setSelectedTypesSet] = useAtom($columnSelectedTypes);
  const setOffset = useSetAtom($offset);

  // States
  const [searchQuery, setSearchQuery] = React.useState('');

  // Memoized values
  const selectedTypes = React.useMemo(
    () => Array.from(selectedTypesSet),
    [selectedTypesSet],
  );

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedTypes),
    [selectedTypes],
  );

  const typeOptions: TypeOption[] = React.useMemo(
    () =>
      (['Bill', 'Invoice', 'AdvanceIn', 'AdvanceOut'] as PaymentType[]).map(
        (type) => ({
          id: type,
          label: t.enums.paymentType[type] ?? type,
        }),
      ),
    [t.enums.paymentType],
  );

  const filteredTypeOptions = React.useMemo(
    () =>
      typeOptions.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [typeOptions, searchQuery],
  );

  const selectedTypeOptions = React.useMemo(
    () => typeOptions.filter((option) => selectedTypes.includes(option.id)),
    [typeOptions, selectedTypes],
  );

  // Callbacks
  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedTypesSet(new Set(Array.from(keys) as PaymentType[]));
      setOffset(0);
    },
    [setSelectedTypesSet, setOffset],
  );

  const handleReset = React.useCallback(() => {
    setSelectedTypesSet(new Set());
    setOffset(0);
  }, [setSelectedTypesSet, setOffset]);

  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const getLabel = React.useCallback(
    (item: TypeOption, isSelected: boolean) => (
      <FilterLabel label={item.label} isSelected={isSelected} />
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@PaymentTypeColumnFilter min-w-[250px]"
      entries={filteredTypeOptions}
      selectedEntries={selectedTypeOptions}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(type) => type.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.payments.list.search.placeholder}
      isLoading={false}
      isFetching={false}
    />
  );
};
